import { FlagModel, ValueModel } from '@zajno/common-mobx/viewModels';
import { createLogger } from 'common/logger';

const logger = createLogger('[LoadMoreViewModel]');

export class LoadMoreViewModel<T> {
    private _isInitialized: boolean = false;

    private _initialPage: number;
    private _limit: number;
    private _callback: (offset: number, limit: number) => Promise<T[]>;
    private _page: ValueModel<number>;
    private readonly _data = new ValueModel<T[]>([], true);
    private readonly _hasMore = new FlagModel(true);
    private readonly _isLoading = new FlagModel(false);

    private _counter = 0;

    initialize = (
        initialPage: number,
        limit: number,
        callback: (offset: number, limit: number) => Promise<T[]>,
    ) => {
        this._callback = callback;

        if (this._isInitialized) {
            return;
        }

        this._initialPage = initialPage;
        this._limit = limit;

        this._page = new ValueModel<number>(initialPage, true);

        this._getData();

        this._isInitialized = true;
    };

    private _getData = async () => {
        try {
            this._isLoading.setTrue();

            this._counter++;
            const currentCount = this._counter;

            const newItems = await this._callback(
                this._page.value * this._limit,
                this._limit,
            );

            if (currentCount !== this._counter) {
                return;
            }

            if (newItems.length < this._limit) {
                this._hasMore.setFalse();
            } else {
                this._hasMore.setTrue();
            }

            this._data.setValue([...this._data.value, ...newItems]);
        } catch (e) {
            logger.error(e);
        } finally {
            this._isLoading.setFalse();
        }
    };

    get items() { return this._data.value; }
    get isLoading() { return this._isLoading.value; }

    public loadMore = () => {
        this._page.setValue(this._page.value + 1);
        this._getData();
    };

    get hasMore() { return this._hasMore.value; }

    public reset = () => {
        this._data.setValue([]);
        this._page.setValue(this._initialPage);
        this._getData();
    };
}
